"use client";

import { c as _c } from "react/compiler-runtime";
import { CreateDropWaveWrapper } from "@/components/waves/CreateDropWaveWrapper";
import { WaveDropsAllWithoutProvider } from "@/components/waves/drops/wave-drops-all";
import { WaveGallery } from "@/components/waves/gallery";
import MobileMemesArtSubmissionBtn from "@/components/waves/memes/submission/MobileMemesArtSubmissionBtn";
import PrivilegedDropCreator, { DropMode } from "@/components/waves/PrivilegedDropCreator";
import { useNotificationsContext } from "@/components/notifications/NotificationsContext";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { UnreadDividerProvider, useUnreadDivider } from "@/contexts/wave/UnreadDividerContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiWave } from "@/generated/models/ApiWave";
import { getHomeRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { resolveWaveSubmissionExperience, WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useWave } from "@/hooks/useWave";
import type { WaveViewMode } from "@/hooks/useWaveViewMode";
import { selectEditingDropId } from "@/store/editSlice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { ActiveDropAction } from "@/types/dropInteractionTypes";
import { commonApiPostWithoutBodyAndResponse } from "@/services/api/common-api";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLayout } from "./layout/LayoutContext";
interface InitialDropState {
  readonly waveId: string;
  readonly serialNo: number;
  readonly dividerSerialNo: number | null;
}
interface MyStreamWaveChatProps {
  readonly wave: ApiWave;
  readonly firstUnreadSerialNo: number | null;
  readonly viewMode: WaveViewMode;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
interface WaveChatLeaveHandlerProps {
  readonly waveId: string;
}
const WaveChatLeaveHandler: React.FC<WaveChatLeaveHandlerProps> = t0 => {
  const $ = _c(6);
  const {
    waveId
  } = t0;
  const {
    setUnreadDividerSerialNo
  } = useUnreadDivider();
  const {
    removeWaveDeliveredNotifications
  } = useNotificationsContext();
  const {
    invalidateNotifications
  } = useContext(ReactQueryWrapperContext);
  let t1;
  let t2;
  if ($[0] !== invalidateNotifications || $[1] !== removeWaveDeliveredNotifications || $[2] !== setUnreadDividerSerialNo || $[3] !== waveId) {
    t1 = () => () => {
      setUnreadDividerSerialNo(null);
      (async () => {
        ;
        try {
          await Promise.resolve(removeWaveDeliveredNotifications(waveId));
        } catch (t3) {
          const error = t3;
          console.error("Failed to remove wave delivered notifications:", error);
        }
        ;
        try {
          await commonApiPostWithoutBodyAndResponse({
            endpoint: `notifications/wave/${waveId}/read`
          });
          invalidateNotifications();
        } catch (t4) {
          const error_0 = t4;
          console.error("Failed to mark feed as read:", error_0);
        }
      })();
    };
    t2 = [waveId, setUnreadDividerSerialNo, removeWaveDeliveredNotifications, invalidateNotifications];
    $[0] = invalidateNotifications;
    $[1] = removeWaveDeliveredNotifications;
    $[2] = setUnreadDividerSerialNo;
    $[3] = waveId;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  return null;
};
const MyStreamWaveChat: React.FC<MyStreamWaveChatProps> = t0 => {
  const $ = _c(68);
  const {
    wave,
    firstUnreadSerialNo,
    viewMode,
    onDropClick
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const containerRef = useRef(null);
  const {
    isMemesWave,
    isCurationWave
  } = useWave(wave);
  const t1 = wave.participation.submission_strategy ?? null;
  let t2;
  if ($[0] !== isCurationWave || $[1] !== isMemesWave || $[2] !== t1) {
    t2 = resolveWaveSubmissionExperience({
      isMemesWave,
      isCurationWave,
      submissionStrategy: t1
    });
    $[0] = isCurationWave;
    $[1] = isMemesWave;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const submissionExperience = t2;
  const editingDropId = useSelector(selectEditingDropId);
  const {
    isApp
  } = useDeviceInfo();
  let t3;
  if ($[4] !== wave.id) {
    t3 = {
      waveId: wave.id,
      activeDrop: null
    };
    $[4] = wave.id;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [activeDropState, setActiveDropState] = useState(t3);
  const activeDrop = activeDropState.waveId === wave.id ? activeDropState.activeDrop : null;
  let t4;
  if ($[6] !== wave.id) {
    t4 = nextActiveDrop => {
      setActiveDropState({
        waveId: wave.id,
        activeDrop: nextActiveDrop
      });
    };
    $[6] = wave.id;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const setActiveDropForWave = t4;
  let t5;
  if ($[8] !== firstUnreadSerialNo || $[9] !== searchParams || $[10] !== wave.id) {
    bb0: {
      const dropParam = searchParams.get("serialNo");
      if (!dropParam) {
        t5 = null;
        break bb0;
      }
      const parsed = Number.parseInt(dropParam, 10);
      if (!Number.isFinite(parsed)) {
        t5 = null;
        break bb0;
      }
      let t6;
      if ($[12] !== firstUnreadSerialNo || $[13] !== searchParams) {
        const dividerParam = searchParams.get("divider");
        const dividerParsed = dividerParam ? Number.parseInt(dividerParam, 10) : null;
        t6 = dividerParsed !== null && Number.isFinite(dividerParsed) ? dividerParsed : firstUnreadSerialNo;
        $[12] = firstUnreadSerialNo;
        $[13] = searchParams;
        $[14] = t6;
      } else {
        t6 = $[14];
      }
      const dividerSerialNo = t6;
      let t7;
      if ($[15] !== dividerSerialNo || $[16] !== parsed || $[17] !== wave.id) {
        t7 = {
          waveId: wave.id,
          serialNo: parsed,
          dividerSerialNo
        };
        $[15] = dividerSerialNo;
        $[16] = parsed;
        $[17] = wave.id;
        $[18] = t7;
      } else {
        t7 = $[18];
      }
      t5 = t7;
    }
    $[8] = firstUnreadSerialNo;
    $[9] = searchParams;
    $[10] = wave.id;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const initialDropState = t5;
  let t6;
  if ($[19] !== firstUnreadSerialNo || $[20] !== initialDropState || $[21] !== wave.id) {
    t6 = () => {
      if (initialDropState?.waveId === wave.id && initialDropState.dividerSerialNo !== null) {
        return {
          waveId: wave.id,
          serialNo: initialDropState.dividerSerialNo
        };
      }
      return {
        waveId: wave.id,
        serialNo: firstUnreadSerialNo
      };
    };
    $[19] = firstUnreadSerialNo;
    $[20] = initialDropState;
    $[21] = wave.id;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const [capturedDividerState] = useState(t6);
  const scrollTarget = initialDropState?.waveId === wave.id ? initialDropState.serialNo : null;
  let dividerTarget = firstUnreadSerialNo;
  if (initialDropState?.waveId === wave.id) {
    dividerTarget = initialDropState.dividerSerialNo;
  } else {
    if (capturedDividerState.waveId === wave.id) {
      dividerTarget = capturedDividerState.serialNo;
    }
  }
  let t7;
  let t8;
  if ($[23] !== initialDropState || $[24] !== pathname || $[25] !== router || $[26] !== searchParams) {
    t7 = () => {
      if (!initialDropState) {
        return;
      }
      const params = new URLSearchParams(searchParams.toString() || "");
      if (!params.has("serialNo") && !params.has("divider")) {
        return;
      }
      params.delete("serialNo");
      params.delete("divider");
      const href = params.toString() ? `${pathname}?${params.toString()}` : pathname || getHomeRoute();
      router.replace(href, {
        scroll: false
      });
    };
    t8 = [initialDropState, searchParams, router, pathname];
    $[23] = initialDropState;
    $[24] = pathname;
    $[25] = router;
    $[26] = searchParams;
    $[27] = t7;
    $[28] = t8;
  } else {
    t7 = $[27];
    t8 = $[28];
  }
  useEffect(t7, t8);
  const {
    waveViewStyle
  } = useLayout();
  let t9;
  if ($[29] !== setActiveDropForWave) {
    const onReply = (drop, partId) => {
      setActiveDropForWave({
        action: ActiveDropAction.REPLY,
        drop,
        partId
      });
    };
    t9 = t10 => {
      const {
        drop: drop_0,
        partId: partId_0
      } = t10;
      onReply(drop_0, partId_0);
    };
    $[29] = setActiveDropForWave;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const handleReply = t9;
  let t10;
  if ($[31] !== setActiveDropForWave) {
    t10 = () => {
      setActiveDropForWave(null);
    };
    $[31] = setActiveDropForWave;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const onCancelReplyQuote = t10;
  if (viewMode === "gallery") {
    let t11;
    if ($[33] !== onDropClick || $[34] !== wave) {
      t11 = <WaveGallery wave={wave} onDropClick={onDropClick} />;
      $[33] = onDropClick;
      $[34] = wave;
      $[35] = t11;
    } else {
      t11 = $[35];
    }
    let t12;
    if ($[36] !== t11 || $[37] !== waveViewStyle) {
      t12 = <div ref={containerRef} className="tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden" style={waveViewStyle}>{t11}</div>;
      $[36] = t11;
      $[37] = waveViewStyle;
      $[38] = t12;
    } else {
      t12 = $[38];
    }
    return t12;
  }
  const t11 = dividerTarget ?? null;
  const t12 = `unread-divider-${wave.id}`;
  let t13;
  if ($[39] !== wave.id) {
    t13 = <WaveChatLeaveHandler waveId={wave.id} />;
    $[39] = wave.id;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== activeDrop || $[42] !== dividerTarget || $[43] !== handleReply || $[44] !== scrollTarget || $[45] !== wave.id || $[46] !== wave.metrics.muted || $[47] !== wave.metrics.your_unread_drops_count) {
    t14 = <WaveDropsAllWithoutProvider key={wave.id} waveId={wave.id} onReply={handleReply} activeDrop={activeDrop} initialDrop={scrollTarget} dividerSerialNo={dividerTarget} unreadCount={wave.metrics.your_unread_drops_count} dropId={null} isMuted={wave.metrics.muted} />;
    $[41] = activeDrop;
    $[42] = dividerTarget;
    $[43] = handleReply;
    $[44] = scrollTarget;
    $[45] = wave.id;
    $[46] = wave.metrics.muted;
    $[47] = wave.metrics.your_unread_drops_count;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] !== activeDrop || $[50] !== editingDropId || $[51] !== isApp || $[52] !== onCancelReplyQuote || $[53] !== wave) {
    t15 = !(isApp && editingDropId) && <div className="tw-mt-auto"><CreateDropWaveWrapper><PrivilegedDropCreator activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} onDropAddedToQueue={onCancelReplyQuote} wave={wave} dropId={null} fixedDropMode={DropMode.BOTH} /></CreateDropWaveWrapper></div>;
    $[49] = activeDrop;
    $[50] = editingDropId;
    $[51] = isApp;
    $[52] = onCancelReplyQuote;
    $[53] = wave;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  let t16;
  if ($[55] !== submissionExperience || $[56] !== wave) {
    t16 = submissionExperience === WaveSubmissionExperience.MEMES_LEGACY && <MobileMemesArtSubmissionBtn wave={wave} />;
    $[55] = submissionExperience;
    $[56] = wave;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  let t17;
  if ($[58] !== t14 || $[59] !== t15 || $[60] !== t16 || $[61] !== waveViewStyle) {
    t17 = <div ref={containerRef} className="tw-w-full tw-flex tw-flex-col tw-overflow-y-auto tw-overflow-x-hidden lg:tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300 scroll-shadow tw-flex-grow" style={waveViewStyle}>{t14}{t15}{t16}</div>;
    $[58] = t14;
    $[59] = t15;
    $[60] = t16;
    $[61] = waveViewStyle;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  let t18;
  if ($[63] !== t11 || $[64] !== t12 || $[65] !== t13 || $[66] !== t17) {
    t18 = <UnreadDividerProvider initialSerialNo={t11} key={t12}>{t13}{t17}</UnreadDividerProvider>;
    $[63] = t11;
    $[64] = t12;
    $[65] = t13;
    $[66] = t17;
    $[67] = t18;
  } else {
    t18 = $[67];
  }
  return t18;
};
export default MyStreamWaveChat;